import React, {useMemo} from 'react';
import {Grid, Typography, Box, Avatar, Chip, Paper} from '@mui/material';
import {priorityColors, defaultPriority} from './mockData';

const TicketDetails = ({ticket}) => {
    // Helper functions
    const getInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(n => n[0]).join('').toUpperCase();
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            console.error("Date formatting error:", error);
            return 'Invalid date';
        }
    };

    const getPriorityColor = (priority) => {
        if (!priority) return defaultPriority;
        return priorityColors[priority] || defaultPriority;
    };

    // Normalize assigned service provider info
    const getAssignedProvider = () => {
        // If we have assigned_to as provider_id (from database schema)
        if (ticket.assigned_to) {
            return {
                id: ticket.assignedTo.provider_id,
                first_name: ticket.assignedTo.first_name,
                last_name: ticket.assignedTo.last_name,
                name: ticket.assignedTo.first_name + " " + ticket.assignedTo.first_name || 'Unknown',
                role: ticket.assignedTo.specialization || 'Service Provider',
                specialization: ticket.assignedTo.specialization
            };
        }
        // If we have assignedTo as an object (from within the app)
        else if (ticket.assignedTo) {
            return {
                id: ticket.assignedTo.provider_id,
                first_name: ticket.assignedTo.first_name,
                last_name: ticket.assignedTo.last_name,
                name: ticket.assignedTo.first_name + " " + ticket.assignedTo.first_name || 'Unknown',
                role: ticket.assignedTo.specialization || 'Service Provider',
                specialization: ticket.assignedTo.specialization
            };
        }
        // No provider assigned
        return null;
    };

    const assignedProvider = getAssignedProvider();

    /**
     * Formats Draft.js content state into plain text
     * @param {string|Object} content - Draft.js content state as string or object
     * @returns {string} Formatted plain text
     */
    const formatDraftJsContent = (content) => {
        // If content is empty or undefined, return empty string
        if (!content) return '';

        // Parse the content if it's a string
        let contentState;
        if (typeof content === 'string') {
            try {
                contentState = JSON.parse(content);
            } catch (error) {
                // If not valid JSON, return the original string
                return content;
            }
        } else {
            contentState = content;
        }

        // Check if it's a valid Draft.js content state
        if (!contentState.blocks || !Array.isArray(contentState.blocks)) {
            // If not in expected format, return original content as string
            return typeof content === 'string' ? content : JSON.stringify(content);
        }

        // Extract text from blocks with appropriate line breaks
        const textBlocks = contentState.blocks.map(block => block.text || '');

        // Join blocks with line breaks
        return textBlocks.join('\n');
    };

    /**
     * Checks if a string is a Draft.js content state
     * @param {string} text - Text to check
     * @returns {boolean} True if it appears to be a Draft.js content state
     */
    const isDraftJsContent = (text) => {
        if (!text || typeof text !== 'string') return false;

        // Check for typical Draft.js structure markers
        return text.includes('"blocks"') && text.includes('"entityMap"');
    };

    // Format the description if it's a Draft.js content state
    const formattedDescription = useMemo(() => {
        if (!ticket || !ticket.description) return '';

        if (isDraftJsContent(ticket.description)) {
            return formatDraftJsContent(ticket.description);
        }

        return ticket.description;
    }, [ticket]);

    return (
        <>
            <Grid container spacing={2} sx={{mb: 3}}>
                <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">Tenant</Typography>
                    <Box sx={{display: 'flex', alignItems: 'center', mt: 0.5}}>
                        <Avatar
                            sx={{
                                width: 24,
                                height: 24,
                                fontSize: '0.75rem',
                                mr: 1,
                                bgcolor: '#3B82F6'
                            }}
                        >
                            {getInitials(ticket.tenant?.name || ticket.tenant_name || 'Unknown')}
                        </Avatar>
                        <Box>
                            <Typography
                                variant="body1">{ticket.tenant?.name || ticket.tenant_name || 'Unknown'}</Typography>
                            <Typography
                                variant="caption">{ticket.tenant?.unit || ticket.house_number || ''}</Typography>
                        </Box>
                    </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">Property</Typography>
                    <Typography
                        variant="body1">{ticket.property?.name || ticket.property_name || 'Unknown'}</Typography>
                    <Typography variant="caption">{ticket.property?.address || ''}</Typography>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">Category</Typography>
                    <Typography
                        variant="body1">{ticket.category || ticket.problem_type_name || 'Uncategorized'}</Typography>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">Priority</Typography>
                    <Chip
                        label={ticket.priority || 'Medium'}
                        size="small"
                        sx={{
                            backgroundColor: getPriorityColor(ticket.priority).bg,
                            color: getPriorityColor(ticket.priority).color,
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            mt: 0.5
                        }}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">Assigned To</Typography>
                    {assignedProvider ? (
                        <Box sx={{display: 'flex', alignItems: 'center', mt: 0.5}}>
                            <Avatar
                                sx={{
                                    width: 24,
                                    height: 24,
                                    fontSize: '0.75rem',
                                    mr: 1,
                                    bgcolor: '#10B981'
                                }}
                            >
                                {getInitials(assignedProvider.name)}
                            </Avatar>
                            <Box>
                                <Typography variant="body1">{assignedProvider.name}</Typography>
                                <Typography
                                    variant="caption">{assignedProvider.specialization || assignedProvider.role}</Typography>
                            </Box>
                        </Box>
                    ) : (
                        <Typography variant="body1">Unassigned</Typography>
                    )}
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">Last Updated</Typography>
                    <Typography variant="body1">{formatDate(ticket.updatedAt || ticket.date_updated)}</Typography>
                </Grid>

                {(ticket.scheduledDate || ticket.scheduled_date) && (
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="textSecondary">Scheduled Visit</Typography>
                        <Typography
                            variant="body1">{formatDate(ticket.scheduledDate || ticket.scheduled_date)}</Typography>
                    </Grid>
                )}

                {((ticket.estimatedCost && ticket.estimatedCost > 0) ||
                    (ticket.estimated_cost && ticket.estimated_cost > 0)) && (
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="textSecondary">Estimated Cost</Typography>
                        <Typography variant="body1">
                            ${(ticket.estimatedCost || ticket.estimated_cost || 0).toFixed(2)}
                        </Typography>
                    </Grid>
                )}

                {((ticket.actualCost && ticket.actualCost > 0) ||
                    (ticket.actual_cost && ticket.actual_cost > 0)) && (
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="textSecondary">Actual Cost</Typography>
                        <Typography variant="body1">
                            ${(ticket.actualCost || ticket.actual_cost || 0).toFixed(2)}
                        </Typography>
                    </Grid>
                )}
            </Grid>

            <Typography variant="subtitle2" sx={{mb: 1, fontWeight: 600}}>Description</Typography>
            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    mb: 3,
                    backgroundColor: '#F9FAFB',
                    borderRadius: '8px'
                }}
            >
                <Typography variant="body2">
                    {formattedDescription.split('\n').map((paragraph, index) => (
                        <Typography key={index} variant="body2"
                                    paragraph={index < formattedDescription.split('\n').length - 1}>
                            {paragraph || '\u00A0'}
                        </Typography>
                    )) || "No description provided"}</Typography>
            </Paper>
        </>
    );
};

export default TicketDetails;